import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailDripEmailCampaign } from '@feathr/blackbox';
import { Button, CardV2 as Card, Label } from '@feathr/components';
import MailrailAutomationSchedule from '@feathr/extender/App/EventsPage/CampaignEditPage/MailrailAutomationSchedule/MailrailAutomationSchedule';
import MailrailPredicatesConfig from '@feathr/extender/App/EventsPage/CampaignEditPage/MailrailPredicatesConfig/MailrailPredicatesConfig';
import TriggerTypeRadios from '@feathr/extender/App/EventsPage/CampaignEditPage/TriggerTypeRadios/TriggerTypeRadios';
import type { ICampaignValidationErrors } from '@feathr/extender/App/EventsPage/CampaignSummary';
import { useStore } from '@feathr/extender/state';

import EmailTemplateConfig from '../EmailTemplateConfig';
import MailrailMergeDataInputs from '../MailrailMergeDataInputs';
import SenderInformationConfig from '../SenderInformationConfig';
import { useMailrailTriggerType } from '../useMailrailTriggerType';

import * as styles from './EnrollmentCardEditor.css';

interface IEnrollmentCardEditorProps {
  campaign: MailrailDripEmailCampaign;
  validationErrors: ICampaignValidationErrors;
  disabled?: boolean;
  onDone: () => void;
}

/**
 * The editor for card 0 of the drip Builder — the campaign in its role as as step 0. It composes
 * the enrollment-trigger leaf components (the same ones the Automation step uses), the shared
 * mailing config, and the enrollment delay. For card 0 the campaign IS the mailing, so it is passed
 * as both the `campaign` and `mailing` props of the sender/template config.
 *
 * @param props - {@link IEnrollmentCardEditorProps}.
 */
function EnrollmentCardEditor({
  campaign,
  validationErrors,
  disabled = false,
  onDone,
}: Readonly<IEnrollmentCardEditorProps>): JSX.Element {
  const { t } = useTranslation();
  const { triggerTypeOptions, handleSubtypeChange } = useMailrailTriggerType(campaign);
  const { Templates } = useStore();
  const isTimeSubtype = campaign.get('subtype') === 'time';
  const templateId = campaign.get('template_id');
  const template = templateId ? Templates.get(templateId) : undefined;

  return (
    <Card theme={'disabled'} width={'full'}>
      <Card.Content addVerticalGap={true}>
        <div className={styles.overrideGap}>
          <Label weight={'medium'}>{t('Enrollment Automation')}</Label>
        </div>
        <TriggerTypeRadios
          context={'drip'}
          disabled={disabled}
          handleSubtypeChange={handleSubtypeChange}
          subtype={campaign.get('subtype')}
          triggerTypeOptions={triggerTypeOptions}
        />
        <MailrailPredicatesConfig campaign={campaign} disabled={disabled} variant={'well'} />
      </Card.Content>
      {!isTimeSubtype && (
        <Card.Content>
          <Label weight={'medium'}>{t('Schedule')}</Label>
          <MailrailAutomationSchedule
            campaign={campaign}
            disabled={disabled}
            validationErrors={validationErrors}
          />
        </Card.Content>
      )}
      <Card.Content>
        <Label weight={'medium'}>{t('Sender Information')}</Label>
        <SenderInformationConfig
          disabled={disabled}
          mailing={campaign}
          validationErrors={validationErrors}
          variant={'well'}
        />
      </Card.Content>
      <Card.Content>
        <Label weight={'medium'}>{t('Email Template')}</Label>
        <EmailTemplateConfig
          campaign={campaign}
          disabled={disabled}
          mailing={campaign}
          template={template}
          validationErrors={validationErrors}
          variant={'well'}
        />
      </Card.Content>
      <Card.Content>
        <Label weight={'medium'}>{t('Default Merge Data')}</Label>
        <MailrailMergeDataInputs disabled={disabled} template={template} />
      </Card.Content>
      {!disabled && (
        <Card.Actions>
          <Button name={'done-enrollment'} onClick={onDone} type={'primary'}>
            {t('Done')}
          </Button>
        </Card.Actions>
      )}
    </Card>
  );
}

export default observer(EnrollmentCardEditor);
